import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../components/ImageUpload';
import { 
  User, 
  MapPin, 
  Flag, 
  Calendar,
  Edit2,
  Save,
  X,
  Loader2,
  Trophy,
  Users,
  MessageSquare,
  Camera
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProfilePage = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user, golfer: currentGolfer, updateGolferProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [isFollowing, setIsFollowing] = useState(false);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [followLoading, setFollowLoading] = useState(false);
  
  const isOwnProfile = !uid || uid === user?.uid;
  const profileUid = uid || user?.uid;

  useEffect(() => {
    fetchProfile();
    if (profileUid) {
      fetchFollowCounts();
      if (user && !isOwnProfile) {
        checkFollowStatus();
      }
    }
  }, [uid, user]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      if (isOwnProfile) {
        setProfile(currentGolfer);
      } else {
        const response = await axios.get(`${API_URL}/golfers/${uid}`);
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowCounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/follow/counts/${profileUid}`);
      setFollowCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch follow counts:', error);
    }
  };

  const checkFollowStatus = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/follow/check/${uid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFollowing(response.data.is_following);
    } catch (error) {
      console.error('Failed to check follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setFollowLoading(true);
    try {
      const token = await user.getIdToken();
      if (isFollowing) {
        await axios.delete(`${API_URL}/follow/golfer/${uid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(false);
        setFollowCounts(prev => ({ ...prev, followers: Math.max(0, prev.followers - 1) }));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API_URL}/follow/golfer/${uid}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(true);
        setFollowCounts(prev => ({ ...prev, followers: prev.followers + 1 }));
        toast.success('Following!');
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/messages', { state: { startDmWith: uid, recipientName: profile?.display_name } });
  };

  const handleEdit = () => {
    setEditData({
      display_name: profile?.display_name || '',
      bio: profile?.bio || '',
      home_city: profile?.home_city || '',
      home_state: profile?.home_state || '',
      handicap: profile?.handicap || '',
      skill_level: profile?.skill_level || 'intermediate',
      photo_url: profile?.photo_url || ''
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateGolferProfile({
        ...editData,
        handicap: editData.handicap ? parseFloat(editData.handicap) : null,
        photo_url: editData.photo_url || null
      });
      setProfile(updated);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const skillLevels = ['beginner', 'intermediate', 'advanced', 'competitive'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-16">
        <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-display text-white mb-2">Profile Not Found</h2>
        <p className="text-cream/60">This golfer profile doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <Card className="bg-gradient-to-r from-fairway to-deep-green border-0">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Avatar */}
            <div className="relative w-24 h-24 shrink-0">
              <div className="w-24 h-24 rounded-full bg-gold/20 flex items-center justify-center overflow-hidden">
                {(editData.photo_url || profile.photo_url) ? (
                  <img 
                    src={editData.photo_url || profile.photo_url} 
                    alt={profile.display_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-gold font-display font-bold text-4xl">
                    {profile.display_name?.charAt(0) || 'G'}
                  </span>
                )}
              </div>
              {editing && isOwnProfile && (
                <div className="absolute -bottom-2 -right-2">
                  <label className="w-8 h-8 bg-gold rounded-full flex items-center justify-center cursor-pointer hover:bg-gold/80 transition-colors">
                    <Camera className="w-4 h-4 text-deep-green" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const { uploadFile } = await import('../firebase');
                            const timestamp = Date.now();
                            const path = `profile_photos/${user.uid}/${timestamp}_${file.name}`;
                            const url = await uploadFile(file, path, null);
                            setEditData({ ...editData, photo_url: url });
                            toast.success('Photo uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload photo');
                          }
                        }
                      }}
                      data-testid="profile-photo-input"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              {editing ? (
                <Input
                  value={editData.display_name}
                  onChange={(e) => setEditData({ ...editData, display_name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white text-2xl font-display font-bold mb-2"
                  data-testid="edit-display-name"
                />
              ) : (
                <h1 className="font-display font-bold text-2xl md:text-3xl text-white mb-2">
                  {profile.display_name}
                </h1>
              )}
              
              <div className="flex flex-wrap items-center gap-4 text-cream/80 text-sm">
                {(profile.home_city || profile.home_state) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {editing ? (
                      <div className="flex gap-2">
                        <Input
                          value={editData.home_city}
                          onChange={(e) => setEditData({ ...editData, home_city: e.target.value })}
                          placeholder="City"
                          className="bg-white/10 border-white/20 text-white w-24 h-8 text-sm"
                        />
                        <Input
                          value={editData.home_state}
                          onChange={(e) => setEditData({ ...editData, home_state: e.target.value })}
                          placeholder="State"
                          className="bg-white/10 border-white/20 text-white w-16 h-8 text-sm"
                        />
                      </div>
                    ) : (
                      `${profile.home_city || ''}, ${profile.home_state || ''}`
                    )}
                  </span>
                )}
                <Badge className="bg-gold/20 text-gold capitalize">
                  {profile.skill_level || 'Golfer'}
                </Badge>
                {profile.handicap && (
                  <Badge variant="outline" className="border-white/30 text-white">
                    Handicap: {profile.handicap}
                  </Badge>
                )}
              </div>
            </div>

            {/* Actions */}
            {isOwnProfile && (
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => setEditing(false)}
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSave}
                      className="btn-gold"
                      disabled={saving}
                      data-testid="save-profile-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={handleEdit}
                    className="border-white/30 text-white hover:bg-white/10"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            )}

            {!isOwnProfile && (
              <div className="flex gap-2">
                <Button 
                  className={isFollowing ? "bg-white/10 text-white hover:bg-white/20" : "btn-gold"}
                  onClick={handleFollow}
                  disabled={followLoading}
                  data-testid="follow-btn"
                >
                  {followLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={handleMessage}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            )}
          </div>
          
          {/* Follower Counts */}
          <div className="flex gap-6 mt-4 pt-4 border-t border-white/10">
            <div className="text-center">
              <p className="font-display font-bold text-2xl text-gold">{followCounts.followers}</p>
              <p className="text-cream/70 text-sm">Followers</p>
            </div>
            <div className="text-center">
              <p className="font-display font-bold text-2xl text-gold">{followCounts.following}</p>
              <p className="text-cream/70 text-sm">Following</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Completion Progress */}
      {isOwnProfile && (
        <Card className="card-premium">
          <CardContent className="p-4">
            {(() => {
              const fields = [
                { key: 'display_name', label: 'Display Name' },
                { key: 'bio', label: 'Bio' },
                { key: 'home_city', label: 'Home City' },
                { key: 'home_state', label: 'Home State' },
                { key: 'handicap', label: 'Handicap' },
                { key: 'skill_level', label: 'Skill Level' },
                { key: 'photo_url', label: 'Profile Photo' },
              ];
              const filled = fields.filter(f => profile?.[f.key]).length;
              const pct = Math.round((filled / fields.length) * 100);
              const missing = fields.filter(f => !profile?.[f.key]).map(f => f.label);
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-deep-green" data-testid="profile-progress-label">
                      Profile {pct}% Complete
                    </span>
                    {pct < 100 && (
                      <Button variant="ghost" size="sm" onClick={handleEdit} className="text-gold text-xs">
                        Complete Profile
                      </Button>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-gold h-2.5 rounded-full transition-all duration-500" 
                      style={{ width: `${pct}%` }}
                      data-testid="profile-progress-bar"
                    />
                  </div>
                  {missing.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Missing: {missing.join(', ')}
                    </p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Bio & Details */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display">About</CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                value={editData.bio}
                onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                placeholder="Tell other golfers about yourself..."
                className="min-h-[100px]"
                data-testid="edit-bio"
              />
            ) : (
              <p className="text-muted-foreground">
                {profile.bio || 'No bio added yet.'}
              </p>
            )}

            {profile.play_style_tags?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-semibold text-deep-green mb-2">Play Style</p>
                <div className="flex flex-wrap gap-2">
                  {profile.play_style_tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="capitalize">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {profile.languages?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-semibold text-deep-green mb-2">Languages</p>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((lang) => (
                    <Badge key={lang} variant="outline">
                      {lang}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Golf Details */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display">Golf Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Skill Level</span>
              {editing ? (
                <select
                  value={editData.skill_level}
                  onChange={(e) => setEditData({ ...editData, skill_level: e.target.value })}
                  className="h-8 px-3 border border-input rounded-sm bg-background text-sm"
                >
                  {skillLevels.map((level) => (
                    <option key={level} value={level} className="capitalize">{level}</option>
                  ))}
                </select>
              ) : (
                <span className="font-semibold text-deep-green capitalize">{profile.skill_level}</span>
              )}
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Handicap</span>
              {editing ? (
                <Input
                  type="number"
                  step="0.1"
                  value={editData.handicap}
                  onChange={(e) => setEditData({ ...editData, handicap: e.target.value })}
                  className="w-20 h-8 text-sm"
                  placeholder="--"
                />
              ) : (
                <span className="font-semibold text-deep-green">{profile.handicap || '--'}</span>
              )}
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Preferred Tees</span>
              <span className="font-semibold text-deep-green capitalize">{profile.preferred_tee || '--'}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Pace of Play</span>
              <span className="font-semibold text-deep-green capitalize">{profile.pace_of_play || '--'}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Travel Frequency</span>
              <span className="font-semibold text-deep-green capitalize">{profile.travel_frequency || '--'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold" />
              Statistics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Rounds Logged</span>
              <span className="font-bold text-2xl text-gold">{profile.stats?.rounds_logged || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Cities Played</span>
              <span className="font-bold text-2xl text-gold">{profile.stats?.cities_played || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Events Joined</span>
              <span className="font-bold text-2xl text-gold">{profile.stats?.events_joined || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        {profile.badges?.length > 0 && (
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-deep-green font-display">Badges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {profile.badges.map((badge) => (
                  <div 
                    key={badge}
                    className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center"
                  >
                    <Trophy className="w-8 h-8 text-gold" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Account Deletion - App Store Compliance */}
      {isOwnProfile && (
        <Card className="border-red-200">
          <CardContent className="p-6">
            <h3 className="font-display font-semibold text-red-700 mb-2">Danger Zone</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete your account? This action is permanent and cannot be undone. All your data, rounds, messages, and bookings will be removed.')) {
                  if (window.confirm('This is your final confirmation. Your account will be flagged for deletion and processed within 30 days. Continue?')) {
                    axios.delete(`${API_URL}/account/delete`, {
                      headers: { Authorization: `Bearer ${user?.accessToken}` }
                    }).then(() => {
                      toast.success('Account flagged for deletion. You will receive a confirmation email.');
                    }).catch(() => {
                      toast.error('Failed to process deletion request. Please contact support@fairwayalliance.org');
                    });
                  }
                }
              }}
              data-testid="delete-account-btn"
            >
              Delete My Account
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfilePage;
